import React from "react";
import { SVG, Quantity, TransparentVideo, UpsellHintText } from "~components";
import { useQuantity, useCartContext, useProductContext } from "~hooks";
import cn from "classnames";
import { OUT_OF_STOCK_TEXT } from "~constants/constants";
import * as styles from "./styles.module.scss";

/** ============================================================================
 * @component
 * Cart item
 *
 * @param  {string}    	variantId  					Variant ID
 * @param  {number}    	quantity  					Amount of this product in cart
 * @return {node}
 */

const CartItem = ({ quantity: initialQuantity, variantId }) => {
  const { quantity } = useQuantity(initialQuantity);
  const {
    removeLineItem,
    updateQuantityOfLineItem,
    getDiscountedItemPrice,
    numberOfItemsInCart,
    hasReachedMaximumDiscount
  } = useCartContext();
  const { getProductData, getVariantData } = useProductContext();

  const variantData = getVariantData(variantId);

  const { productVideo, title, checkoutWarning } = getProductData(
    variantData.productId
  );

  const { inventoryQuantity } = variantData;
  const isItemOutOfStock = quantity > inventoryQuantity;

  const handleRemoveItem = () => {
    removeLineItem(variantId);
  };

  const handleIncrement = () => {
    updateQuantityOfLineItem(variantId, quantity + 1);
  };

  const handleDecrement = () => {
    updateQuantityOfLineItem(variantId, quantity - 1);
  };

  const { amount: preDiscountPrice, currencyCode } = variantData.price;

  const discountedPrice = getDiscountedItemPrice(preDiscountPrice);
  const hasDiscount = preDiscountPrice !== discountedPrice;

  return (
    <div className={styles.container}>
      <div className={styles.imageContainer}>
        <TransparentVideo webM={productVideo?.webM} mov={productVideo?.mov} />
      </div>

      <div className={cn(`b2`, styles.rightContainer)}>
        <div>
          <div className={styles.betweenFlexContainer}>
            <div className={styles.bpBetweenFlexContainer}>
              <div className={styles.column}>
                <div className={styles.productName}>{title}</div>
                <div className={styles.shade}>Shade: {variantData.title}</div>
                {checkoutWarning && (
                  <div className={cn(`caption`, styles.checkoutWarning)}>
                    {checkoutWarning}
                  </div>
                )}
              </div>
              <div className={cn(styles.column, styles.rightCol)}>
                <div className={styles.price}>
                  {hasDiscount && (
                    <>
                      <span>${discountedPrice.toFixed(2)}</span>
                      <span className={styles.strikethrough}>
                        ${preDiscountPrice.toFixed(2)}
                      </span>
                    </>
                  )}

                  {!hasDiscount && <span>${preDiscountPrice.toFixed(2)}</span>}

                  <span>{currencyCode}</span>
                </div>

                {isItemOutOfStock && (
                  <p className={cn(`caption`, styles.warningText)}>
                    {OUT_OF_STOCK_TEXT}
                  </p>
                )}
              </div>
            </div>

            <button
              onClick={handleRemoveItem}
              className={styles.mobileRemoveButton}
              type="button"
            >
              <SVG svg="x" />
            </button>
          </div>
        </div>

        <div>
          {!hasReachedMaximumDiscount(numberOfItemsInCart) && (
            <UpsellHintText className={styles.discountHint} />
          )}

          <div className={styles.betweenFlexContainer}>
            <div className={styles.quantityContainer}>
              <span className={styles.quantityText}>QTY:</span>
              <Quantity
                value={quantity}
                handleDecrement={handleDecrement}
                handleIncrement={handleIncrement}
              />
            </div>
            <button
              className={styles.desktopRemoveButton}
              type="button"
              onClick={handleRemoveItem}
            >
              Remove
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartItem;
